// ════════════════════════════════════════════════════════════════════
// TERROR — THE STORY CUTSCENE
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-18, handing over the Sir H'osh Vrown scene:
//   "dialogue cut scene thing like we have when you make a character,
//    there's gonna be a LOT of this ;3"
//
// ── THE GAP THIS FILLS ───────────────────────────────────────────────
// Every story opponent in the game already carries
// `dialogue: { approach, onWin, onLoss }`. EndlessDepths renders the
// approach line, the Plaza reads the first one — and the WORLD MAP,
// which is Story Mode, played none of it. You walked into a boss node
// and the duel simply started. All the words were already written and
// nobody was saying them.
//
// So this is not a new content system. It is a mouth for the data that
// has been sitting there since July.
//
// ── THE ONE STRUCTURAL DECISION ──────────────────────────────────────
// A line wrapped in [SQUARE BRACKETS] is the PLAYER speaking. Not a
// speaker field, not a second array, not an object per line — a bracket.
//
// Because caP'n writes these as scripts, in one block, in the order they
// are said:
//     SHV: bold of you two to enter...
//     P:   We declined the offer...
//     SHV: *smirks* you're either naive or...
// Any shape that makes him tag every line with a speaker is a shape that
// makes writing a hundred of these worse. A bracket is one keystroke at
// each end and it survives being pasted out of a chat message.
//
// *ASTERISKS* are stage directions and render as italic dimmed action.
// Same reasoning: it is how he already writes them.
//
// ── PLACEHOLDER-SAFE ─────────────────────────────────────────────────
// A missing dialogue block is not an error, it is a node with no scene —
// `hasScene()` says so and the caller skips straight to the duel. That
// is what lets the other 53 nodes stay silent until they have words.
// ════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { GM } from "./theme";
import { readLine } from "./storyScript";

// THE SCRIPT FORMAT lives in storyScript.js — one pure function with no
// JSX around it, so storyScript.selftest.mjs can run it against every
// authored line in the game without a DOM. Re-exported here because
// every existing caller imports { hasScene } from this file and a
// rename that touches six call sites to move one function is a worse
// trade than one line of re-export.
export { readLine, hasScene, NAMED } from "./storyScript";

// Inline *stage directions* inside an otherwise spoken line — caP'n
// writes "*smirks* You're either naive or..." and both halves have to
// render differently in the same sentence.
function Spoken({ text, color }) {
  const parts = String(text).split(/(\*[^*]+\*)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("*") && p.endsWith("*") ? (
          <span key={i} style={{ color: GM.vellumFaint, fontStyle: "italic", opacity: 0.85 }}>
            {p.slice(1, -1)}{" "}
          </span>
        ) : (
          <span key={i} style={{ color }}>{p}</span>
        )
      )}
    </>
  );
}

/**
 * <StoryCutscene
 *    lines={["...", "[We declined the offer.]", "*smirks* You're either naive or..."]}
 *    speaker="Sir H'osh Vrown" speakerTitle="Colonel of Omnis"
 *    playerName="Lyraei & Lestre" accent="#7c3aed"
 *    onDone={() => startTheDuel()} />
 *
 * Advance: click / Enter / Space / Q. Skip the rest: Escape.
 * onDone fires exactly once, however it ends.
 */
export default function StoryCutscene({
  lines = [], speaker = "", speakerTitle = "", playerName = "You",
  accent = "#c8963c", onDone,
}) {
  const script = (lines || []).filter(Boolean).map(readLine).filter((l) => l.text);
  const [i, setI] = useState(0);
  const [done, setDone] = useState(false);

  const finish = useCallback(() => {
    // Once. A cutscene that fires its caller twice starts two duels.
    setDone((was) => { if (!was && typeof onDone === "function") onDone(); return true; });
  }, [onDone]);

  const next = useCallback(() => {
    setI((n) => { if (n + 1 >= script.length) { finish(); return n; } return n + 1; });
  }, [script.length, finish]);

  useEffect(() => { if (!script.length) finish(); }, [script.length, finish]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(); return; }
      if (["Enter", " ", "Spacebar", "q", "Q"].includes(e.key)) { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, finish]);

  if (!script.length || done) return null;
  const line = script[i];
  const isPlayer = line.kind === "player";
  // A named line borrows the plate and drops the scene's title with it —
  // showing "Colonel of Omnis" under somebody else's name is worse than
  // showing no title at all.
  const guest = !isPlayer && line.who ? line.who : "";
  const who = isPlayer ? playerName : (guest || speaker);
  const tint = isPlayer ? GM.inkGoldBright : accent;

  return (
    <div
      onClick={next}
      style={{
        position: "fixed", inset: 0, zIndex: 900, cursor: "pointer",
        background: `radial-gradient(ellipse at 50% 40%, ${accent}14, rgba(4,2,8,0.94) 55%, #040208)`,
        display: "flex", flexDirection: "column", justifyContent: "flex-end",
        fontFamily: GM.font, userSelect: "none",
      }}
    >
      <style>{`
        @keyframes scLine { from { opacity:0; transform: translateY(10px) } to { opacity:1; transform:none } }
        @keyframes scTick { 0%,100% { opacity:.25 } 50% { opacity:1 } }
      `}</style>

      {/* WHO IS TALKING — pinned above the box so it never moves between
          lines. A name that jumps around is a name nobody reads. */}
      <div style={{ padding: "0 clamp(16px, 6vw, 90px)", marginBottom: 10 }}>
        <div style={{
          fontSize: 12, letterSpacing: 5, fontWeight: 900, color: tint,
          textShadow: `0 0 16px ${tint}66`,
        }}>
          {String(who || "").toUpperCase()}
        </div>
        {!isPlayer && !guest && speakerTitle && (
          <div style={{ fontSize: 10, letterSpacing: 3, color: GM.vellumFaint, marginTop: 3, fontStyle: "italic" }}>
            {speakerTitle}
          </div>
        )}
      </div>

      <div style={{
        margin: "0 clamp(16px, 6vw, 90px) clamp(28px, 8vh, 78px)",
        border: `1px solid ${tint}55`,
        borderLeft: `3px solid ${tint}`,
        borderRadius: 6,
        background: "linear-gradient(180deg, rgba(14,9,24,0.96), rgba(7,4,14,0.96))",
        boxShadow: `0 0 34px ${tint}22, 0 14px 44px rgba(0,0,0,0.75)`,
        padding: "clamp(16px, 3vh, 26px) clamp(18px, 4vw, 34px)",
        minHeight: "clamp(88px, 14vh, 130px)",
        display: "flex", flexDirection: "column", justifyContent: "center",
      }}>
        <div key={i} style={{
          fontSize: "clamp(15px, 2.1vw, 21px)", lineHeight: 1.65,
          fontStyle: "italic", fontFamily: GM.fontBody,
          animation: "scLine 320ms cubic-bezier(.16,1,.3,1) both",
        }}>
          {line.kind === "action"
            ? <span style={{ color: GM.vellumFaint, opacity: 0.9 }}>{line.text}</span>
            : <Spoken text={line.text} color={isPlayer ? GM.vellum : GM.vellumDim} />}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
          <span style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint }}>
            {i + 1} / {script.length}
          </span>
          <span style={{ marginLeft: "auto", fontSize: 9, letterSpacing: 2, color: GM.vellumFaint }}>
            ESC TO SKIP
          </span>
          <span style={{ fontSize: 13, color: tint, animation: "scTick 1.15s ease-in-out infinite" }}>▸</span>
        </div>
      </div>
    </div>
  );
}
